import jwt_decode from "jwt-decode";

// Deep-search a decoded token for a value stored under any of the given keys,
// at any nesting level. Needed because admin tokens may wrap the payload
// (e.g. { user: { _id } } or { data: { id } }).
const findInToken = (keys) => {
  try {
    const token = localStorage.getItem("adminToken");
    if (!token) return null;
    const decoded = jwt_decode(token);
    const seen = new Set();
    const stack = [decoded];
    while (stack.length) {
      const node = stack.pop();
      if (!node || typeof node !== "object" || seen.has(node)) continue;
      seen.add(node);
      for (const key of keys) {
        const val = node[key];
        if (val != null && val !== "") return val;
      }
      for (const val of Object.values(node)) {
        if (val && typeof val === "object") stack.push(val);
      }
    }
    return null;
  } catch {
    return null;
  }
};

export const getCurrentAdminEmail = () => findInToken(["email"]);

export const getCurrentAdminId = () =>
  findInToken(["id", "_id", "userId", "adminId"]);

export const normalizeCount = (payload) => {
  const data = payload?.data?.data || payload?.data || payload;
  return data?.count ?? data?.total ?? data?.totalCount ?? data?.totalRecords ?? null;
};

export const endpointMap = {
  users: {
    list:   "api/admin/get-users",
    create: "api/admin/add-users",
    update: "api/admin/edit-users",
    remove: "api/admin/delete-users",
    // `api/admin/users/:id/pause` | `.../resume`
    pause:  "api/admin/users",
  },
  admins: {
    list:   "api/admin/get-admins",
    create: "api/admin/add-admins",
    update: "api/admin/update-admins",
    remove: "api/admin/delete-admins",
  },
  dashboard: "api/admin/get-dashboard",
};

export const userFields = [
  { name: "firstName", label: "First name", placeholder: "Enter first name", required: true },
  { name: "lastName", label: "Last name", placeholder: "Enter last name", required: true },
  { name: "email", label: "Email", type: "email", placeholder: "Enter email address", required: true },
  { name: "password", label: "Password", type: "password", placeholder: "Enter password", createOnly: true },
  { name: "balance", label: "Balance", type: "number", placeholder: "0" },
];

export const adminFields = [
  { name: "firstName", label: "First name", placeholder: "Enter first name", required: true },
  { name: "lastName", label: "Last name", placeholder: "Enter last name", required: true },
  { name: "email", label: "Email", type: "email", placeholder: "Enter email address", required: true },
  { name: "password", label: "Password", type: "password", placeholder: "Enter password", createOnly: true },
  { name: "role", hidden: true },
];

export const defaultUserForm = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  balance: "",
};

export const defaultAdminForm = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  role: "admin",
};

export const getFullName = (record) => {
  const name = `${record.firstName || ""} ${record.lastName || ""}`.trim();
  return name || record.name || record.username || "No name";
};

export const getInitials = (record) => {
  const fullName = getFullName(record);
  return fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

export const getStatus = (record) => {
  if (record.isBlocked) return "blocked";
  const status = record.status;
  if (status) {
    const str = typeof status === "object" ? (status?.title || status?.name || "") : String(status);
    if (str) return str.toLowerCase();
  }
  if (record.isActive === false) return "pending";
  return "active";
};

export const normalizeList = (payload, key) => {
  const data = payload?.data?.data || payload?.data || payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.records)) return data.records;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const normalizeStats = (payload, users, admins) => {
  const data = payload?.data?.data || payload?.data || {};
  return {
    totalUsers: data.totalUsers ?? data.usersCount ?? users.length,
    activeUsers:
      data.activeUsers ?? users.filter((item) => getStatus(item) === "active").length,
    totalAdmins: data.totalAdmins ?? data.adminsCount ?? admins.length,
    blockedUsers:
      data.blockedUsers ??
      users.filter((item) => getStatus(item) === "blocked").length,
  };
};

export const normalizeDashboard = (payload) => {
  const data = payload?.data?.data || payload?.data || {};
  return {
    summary: data.summary || {},
    charts: data.charts || {},
    filter: data.filter || {},
  };
};

export const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};
